/**
 * API client with auth handling, error parsing, retry on 429.
 */

import type { ApiResponse } from '@elon-tools/shared';

const BASE = '/api/v1';
const MAX_RETRIES = 2;

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  options: RequestInit & { _noRedirect?: boolean; _timeout?: number } = {},
  retries = 0,
): Promise<T> {
  const timeout = options._timeout ?? 60000; // 60s default timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...options,
      signal: controller.signal,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // 429: retry with backoff (cap delay at 5s to avoid long waits)
    if (res.status === 429 && retries < MAX_RETRIES) {
      const retryAfter = Math.min(parseInt(res.headers.get('Retry-After') ?? '2', 10), 5);
      await new Promise((r) => setTimeout(r, retryAfter * 1000));
      return request<T>(path, options, retries + 1);
    }

    // 401: redirect unless caller opts out
    if (res.status === 401) {
      if (!options._noRedirect && !window.location.pathname.startsWith('/chat')) {
        window.location.href = '/chat';
      }
      throw new ApiError('AUTH_MISSING_SESSION', 'Sessão expirada', 401);
    }

    const data = (await res.json()) as ApiResponse<T>;

    if (!data.success || data.error) {
      throw new ApiError(
        data.error?.code ?? 'UNKNOWN',
        data.error?.message ?? 'Erro desconhecido',
        res.status,
        data.error?.details,
      );
    }

    return data.data as T;
  } catch (e: any) {
    // AbortController fires DOMException (name: 'AbortError') or TypeError with 'aborted'
    const isAbort =
      (e instanceof DOMException && e.name === 'AbortError') ||
      (e instanceof TypeError && e.message.includes('aborted')) ||
      (e?.name === 'AbortError') ||
      (typeof e?.message === 'string' && /abort/i.test(e.message));
    if (isAbort) {
      throw new ApiError('REQUEST_TIMEOUT', `A requisição expirou após ${Math.round(timeout / 1000)}s. A operação pode ainda estar em andamento no servidor.`, 408);
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Convenience methods
export const api = {
  get: <T>(path: string) => request<T>(path),
  /** GET without 401 redirect (for auth checks) */
  getQuiet: <T>(path: string) => request<T>(path, { _noRedirect: true } as any),
  post: <T>(path: string, body?: unknown, opts?: { timeout?: number }) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined, _timeout: opts?.timeout } as any),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/**
 * Stream SSE from an endpoint. Calls onChunk for each text chunk.
 */
export async function streamApi(
  path: string,
  body: unknown,
  onChunk: (text: string) => void,
  onDone?: (executionId: string | null) => void,
): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = (await res.json()) as ApiResponse;
    throw new ApiError(err.error?.code ?? 'UNKNOWN', err.error?.message ?? 'Erro', res.status);
  }

  const execId = res.headers.get('X-Execution-Id');
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const text = decoder.decode(value, { stream: true });
    for (const line of text.split('\n')) {
      if (line.startsWith('data: ') && !line.includes('[DONE]')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (data.response) onChunk(data.response);
        } catch { /* skip */ }
      }
    }
  }

  onDone?.(execId);
}

export { ApiError };
